'use client';

import { useEffect, useMemo, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import type { LeaderboardRankMeta } from '@impact/types';

function relativeUnit(ageMs: number): { value: number; unit: Intl.RelativeTimeFormatUnit } {
  const seconds = Math.max(0, Math.floor(ageMs / 1000));

  if (seconds < 60) return { value: -seconds, unit: 'second' };
  if (seconds < 60 * 60) return { value: -Math.floor(seconds / 60), unit: 'minute' };
  if (seconds < 60 * 60 * 24) return { value: -Math.floor(seconds / (60 * 60)), unit: 'hour' };
  return { value: -Math.floor(seconds / (60 * 60 * 24)), unit: 'day' };
}

export function RankFreshness({ rankMeta }: { rankMeta?: LeaderboardRankMeta }) {
  const t = useTranslations('leaderboard');
  const locale = useLocale();
  const [now, setNow] = useState<number | null>(null);

  useEffect(() => {
    if (!rankMeta?.computedAt) return;

    setNow(Date.now());
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [rankMeta?.computedAt]);

  const label = useMemo(() => {
    if (!rankMeta?.computedAt || now === null) return null;

    const computedAtMs = Date.parse(rankMeta.computedAt);
    if (!Number.isFinite(computedAtMs)) return null;

    const relative = relativeUnit(now - computedAtMs);
    const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    return t('lastUpdated', { age: formatter.format(relative.value, relative.unit) });
  }, [locale, now, rankMeta?.computedAt, t]);

  if (!label) return null;

  return <span className="text-xs text-stone-400">{label}</span>;
}
